import React, { useState, useMemo, useContext, useEffect, useRef } from "react";
import axios from "axios";
import { useHiringRequests } from "../../contexts/HiringRequestsContext";
import { useCampaigns } from "../../contexts/CampaignsContext";
import Modal from "../../components/UI/Modal";
import HiringRequestForm from "../../components/Form/HiringRequestForm";
import CampaignForm from "../../components/Form/CampaignForm";
import ReusableTable from "../../components/UI/ReusableTable";
import "react-loading-skeleton/dist/skeleton.css";
import { FaExchangeAlt } from "react-icons/fa";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import ReactPaginate from "react-paginate";
import Drawer from "../../components/UI/Drawer";
import HiringRequestDetails from "./RequestDetails";
import { AuthContext } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { MoreVertical, AlertTriangle, Search, X } from "lucide-react";
import { createPortal } from "react-dom";
import StatusBadge from "../../components/UI/StatusBadge";

// Custom Alert Dialog Component
const AlertDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Delete",
  cancelText = "Cancel",
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-end md:items-center z-[1001] backdrop-blur-sm">
      <div className="bg-white dark:bg-black p-6 rounded-t-lg md:rounded-lg shadow-lg w-full max-w-sm mx-auto transition-transform duration-300">
        <h2 className="text-lg font-semibold text-center dark:text-white">
          Are you sure?
        </h2>
        <p className="text-gray-600 mt-2 text-center dark:text-gray-500">
          This action cannot be undone.
        </p>
        <div className="mt-4 flex justify-between space-x-3">
          <button
            className="block w-full py-2 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-10 py-2 text-white bg-red-600 rounded hover:bg-red-700 w-full"
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const HiringRequests = React.memo(() => {
  const { user, roles } = useContext(AuthContext);
  const isManager = roles.includes("Manager");
  const navigate = useNavigate();
  const {
    requests,
    fetchHiringRequests,
    deleteHiringRequest,
    pagination,
    loading,
  } = useHiringRequests();
  const { addCampaign } = useCampaigns();
  const [campaignError, setCampaignError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedHiringRequest, setSelectedHiringRequest] = useState(null);
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [selectedRequestForCampaign, setSelectedRequestForCampaign] =
    useState(null);
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isRevertModalOpen, setIsRevertModalOpen] = useState(false);
  const [revertFeedback, setRevertFeedback] = useState("");
  const [revertRequestId, setRevertRequestId] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const [dropdownCoords, setDropdownCoords] = useState({ x: 0, y: 0 });
  const buttonRefs = useRef({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [selectedRequestForFeedback, setSelectedRequestForFeedback] =
    useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelFeedback, setCancelFeedback] = useState("");
  const [cancelRequestId, setCancelRequestId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchPagination, setSearchPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [isSearching, setIsSearching] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef(null);

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const openDrawerForDetails = (hiringRequest) => {
    setSelectedHiringRequest(hiringRequest);
    setIsDrawerOpen(true);
  };

  const handleDeleteHiringRequest = async (id) => {
    setDeleteId(id);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteHiringRequest(deleteId);
      setNotification({
        message: "Hiring request deleted successfully",
        type: "success",
      });
      // Refresh search results if searching, otherwise regular list
      if (isSearching && searchQuery.trim()) {
        handleSearch(searchQuery, currentPage);
      } else {
        fetchHiringRequests(currentPage);
      }
    } catch (error) {
      setNotification({
        message: "Failed to delete hiring request",
        type: "error",
      });
    } finally {
      setShowDeleteDialog(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteId(null);
  };

  const openCancelModal = (id) => {
    setCancelRequestId(id);
    setCancelFeedback("");
    setIsCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
    setCancelFeedback("");
    setCancelRequestId(null);
  };

  const cancelHiringRequest = async (id, feedback = "") => {
    try {
      const token = localStorage.getItem("authToken");
      const axiosInstance = axios.create({
        baseURL: "https://hrmspayroll.prepstripe.com/api/",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const response = await axiosInstance.post(
        `recruitment-requests/${id}/cancel`,
        {
          feedback: feedback.trim() || undefined,
        }
      );
      if (response?.status === 200 || response?.status === 201) {
        setNotification({
          message:
            response?.data?.message || "Hiring request cancelled successfully.",
          type: "success",
        });
        // Refresh search results if searching, otherwise regular list
        if (isSearching && searchQuery.trim()) {
          handleSearch(searchQuery, currentPage);
        } else {
          fetchHiringRequests(currentPage);
        }
      } else {
        setNotification({
          message:
            response?.data?.message || "Failed to cancel hiring request.",
          type: "error",
        });
      }
    } catch (error) {
      setNotification({
        message:
          error?.response?.data?.message || "Failed to cancel hiring request.",
        type: "error",
      });
    }
  };

  const handleCancelSubmit = async () => {
    if (cancelRequestId) {
      await cancelHiringRequest(cancelRequestId, cancelFeedback);
      closeCancelModal();
    }
  };

  const openFeedbackModal = (request) => {
    setSelectedRequestForFeedback(request);
    setIsFeedbackModalOpen(true);
  };

  const closeFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
    setSelectedRequestForFeedback(null);
  };

  const revertHiringRequest = async (id, feedback) => {
    try {
      const token = localStorage.getItem("authToken");
      const axiosInstance = axios.create({
        baseURL: "https://hrmspayroll.prepstripe.com/api/",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const response = await axiosInstance.post(
        `recruitment-requests/${id}/revert`,
        { feedback }
      );
      if (response?.status === 200 || response?.status === 201) {
        setNotification({
          message:
            response?.data?.message || "Hiring request reverted successfully.",
          type: "success",
        });
        // Refresh search results if searching, otherwise regular list
        if (isSearching && searchQuery.trim()) {
          handleSearch(searchQuery, currentPage);
        } else {
          fetchHiringRequests(currentPage);
        }
      } else {
        setNotification({
          message:
            response?.data?.message || "Failed to revert hiring request.",
          type: "error",
        });
      }
    } catch (error) {
      setNotification({
        message:
          error?.response?.data?.message || "Failed to revert hiring request.",
        type: "error",
      });
    }
  };

  const openRevertModal = (id) => {
    setRevertRequestId(id);
    setRevertFeedback("");
    setIsRevertModalOpen(true);
  };
  const closeRevertModal = () => {
    setIsRevertModalOpen(false);
    setRevertFeedback("");
    setRevertRequestId(null);
  };
  const handleRevertSubmit = async () => {
    if (revertFeedback.trim()) {
      await revertHiringRequest(revertRequestId, revertFeedback.trim());
      closeRevertModal();
    } else {
      setNotification({
        message: "Feedback is required to revert.",
        type: "error",
      });
    }
  };

  const handleClickOutside = (event) => {
    if (!event.target.closest(".dropdown-container")) {
      setOpenDropdownId(null);
    }
  };

  const handleDropdownToggle = (requestId, event) => {
    event.stopPropagation();
    if (openDropdownId === requestId) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(requestId);
      // Calculate portal coordinates
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      const dropdownWidth = 200;
      const dropdownHeight = 180;
      let x = rect.right - dropdownWidth;
      let y = rect.bottom + 4;
      if (x < 0) x = rect.left - dropdownWidth;
      if (y + dropdownHeight > window.innerHeight) {
        y = rect.top - dropdownHeight - 4;
        setDropdownPosition("top");
      } else {
        setDropdownPosition("bottom");
      }
      if (x < 0) x = 8;
      if (y < 0) y = 8;
      setDropdownCoords({ x, y });
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Debounced search effect - triggers search while typing
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If search query is empty, clear search immediately
    if (!searchQuery.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      setSearchPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        perPage: 15,
      });
      setCurrentPage(1);
      // Fetch regular requests when clearing search
      fetchHiringRequests(1);
      return;
    }

    // Set loading state immediately when user starts typing
    setSearchLoading(true);

    // Debounce the search - wait 500ms after user stops typing
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(searchQuery.trim(), 1);
    }, 500);

    // Cleanup timeout on unmount or when searchQuery changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]); // Only depend on searchQuery - handleSearch is stable

  // Memoized Columns Definition
  const columns = useMemo(
    () => [
      {
        label: "S.No.",
        field: "serial_number",
        type: "number",
        render: (row, idx) => {
          const currentPagination = isSearching ? searchPagination : pagination;
          const serialNumber =
            (currentPagination.currentPage - 1) * currentPagination.perPage +
            idx +
            1;
          return <span className="text-center">{serialNumber}</span>;
        },
      },
      {
        label: "Requester",
        field: "requester_name",
        type: "text",
        render: (row) => (
          <button
            type="button"
            onClick={() => openDrawerForDetails(row)}
            className="text-blue-600 hover:underline dark:text-blue-400"
            title="View requester details"
          >
            {row.requester_name}
          </button>
        ),
      },
      { label: "Department", field: "department", type: "text" },
      {
        label: "No. of Positions",
        field: "number_of_positions",
        type: "number",
      },
      {
        label: "Recruitment Status",
        field: "recruitment_request_status",
        type: "text",
        render: (row) => (
          <StatusBadge status={row?.recruitment_request_status} />
        ),
      },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <div className="flex items-center justify-center">
            <div className="relative dropdown-container">
              <button
                ref={(el) => {
                  if (el) buttonRefs.current[row.id] = el;
                }}
                onClick={(e) => handleDropdownToggle(row.id, e)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
            {openDropdownId === row.id &&
              createPortal(
                <div
                  style={{
                    position: "fixed",
                    top: dropdownCoords.y,
                    left: dropdownCoords.x,
                    zIndex: 9999,
                  }}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl min-w-[200px]"
                >
                  <div className="py-1 flex flex-col space-y-0">
                    {/* View Details */}
                    <button
                      onClick={() => {
                        openDrawerForDetails(row);
                        setOpenDropdownId(null);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors block"
                    >
                      View Details
                    </button>
                    {/* For HR role with Reverted status, only show View Details */}
                    {!(
                      roles.includes("HR") &&
                      row.recruitment_request_status === "Reverted"
                    ) && (
                      <>
                        {/* Edit */}
                        {isManager &&
                          user?.id === row.requester_id &&
                          (row.recruitment_request_status === "Pending" ||
                            row.recruitment_request_status === "Reverted") && (
                            <button
                              onClick={() => {
                                openModalForEdit(row);
                                setOpenDropdownId(null);
                              }}
                              className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors block"
                            >
                              Edit
                            </button>
                          )}
                        {/* Cancel */}
                        {isManager &&
                          row.status !== "Approved" &&
                          user?.id === row.requester_id &&
                          (row.recruitment_request_status === "Pending" ||
                            row.recruitment_request_status === "Reverted" ||
                            row.recruitment_request_status === "On Hold") &&
                          row.recruitment_request_status !==
                            "Campaign Created" && (
                            <button
                              className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors block"
                              onClick={() => {
                                openCancelModal(row.id);
                                setOpenDropdownId(null);
                              }}
                            >
                              Cancel
                            </button>
                          )}
                        {/* Feedback - Show only for Reverted status (but not for HR) */}
                        {row.recruitment_request_status === "Reverted" &&
                          !roles.includes("HR") && (
                            <button
                              onClick={() => {
                                openFeedbackModal(row);
                                setOpenDropdownId(null);
                              }}
                              className="w-full text-left px-3 py-2 text-sm text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors block"
                            >
                              Feedback
                            </button>
                          )}
                        {/* Revert */}
                        {roles.includes("HR") &&
                          row.recruitment_request_status === "Approved" && (
                            <button
                              onClick={() => {
                                openRevertModal(row.id);
                                setOpenDropdownId(null);
                              }}
                              className="w-full text-left px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors block"
                            >
                              Revert
                            </button>
                          )}
                        {row.recruitment_request_status === "Approved" &&
                        roles.includes("HR") ? (
                          <button
                            onClick={() => openCampaignModal(row)}
                            className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors block"
                            title="Convert to Campaign"
                          >
                            Convert to Campaign
                          </button>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>,
                document.body
              )}
          </div>
        ),
      },
    ],
    [
      roles,
      isManager,
      user,
      openDropdownId,
      dropdownCoords,
      pagination,
      searchPagination,
      isSearching,
      openDrawerForDetails,
    ]
  );

  const openModalForAdd = () => {
    setEditingRequest(null);
    setIsModalOpen(true);
  };

  const openModalForEdit = (request) => {
    setEditingRequest(request);
    setIsModalOpen(true);
  };

  const openCampaignModal = (request) => {
    const today = new Date();
    const formattedToday = today.toISOString().split("T")[0];
    const campaignData = {
      ...request,
      id: request.id,
      to_date: request.deadline,
      from_date: formattedToday,
      starting_salary_from: "",
      starting_salary_to: "",
      ctc: request.stipend_ctc_per_month || "", // Prefill CTC with stipend data
      position: request.designation || "", // Prefill Position Title from designation (will be converted in form)
      no_of_positions: request.number_of_positions || "", // Prefill No. Of Positions
      workplace: request.location || "", // Prefill Workplace from location (will be converted in form)
    };
    setSelectedRequestForCampaign(campaignData);
    setIsCampaignModalOpen(true);
  };

  const handleCampaignSubmit = async (campaignData) => {
    try {
      // Create axios instance with proper authorization
      const token = localStorage.getItem("authToken");
      const axiosInstance = axios.create({
        baseURL: "https://hrmspayroll.prepstripe.com/api/",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const response = await axiosInstance.post(
        "recruitment-requests/convert-to-campaign",
        {
          id: selectedRequestForCampaign.id,
          ...campaignData, // Send all campaign form data
        }
      );

      if (response?.status === 200 || response?.status === 201) {
        setNotification({
          message: "Hiring request converted to campaign successfully.",
          type: "success",
        });
        setIsCampaignModalOpen(false);
        setSelectedRequestForCampaign(null);
        setCampaignError(null);
        // Refresh the hiring requests list to reflect the changes
        if (isSearching && searchQuery.trim()) {
          handleSearch(searchQuery, currentPage);
        } else {
          fetchHiringRequests(currentPage);
        }

        // Redirect to the newly created campaign details
        const campaignId = response?.data?.data?.id || response?.data?.id;
        if (campaignId) {
          navigate(
            `${getRoleBasedRoute(
              "/admin/campaigns",
              roles
            )}?campaign_id=${campaignId}`
          );
        }
      } else {
        setNotification({
          message: `${
            response?.data?.message ||
            "Unknown error occurred while converting to campaign."
          }`,
          type: "error",
        });
      }
    } catch (error) {
      // console.log("Error converting to campaign:", error);
      setNotification({
        message:
          error?.response?.data?.message ||
          "Failed to convert hiring request to campaign.",
        type: "error",
      });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRequest(null);
  };

  const closeCampaignModal = () => {
    setIsCampaignModalOpen(false);
    setSelectedRequestForCampaign(null);
  };

  // Memoized Exclude Filter Columns
  const excludeFilterColumns = useMemo(
    () => ["actions", "job_description", "justification", "serial_number"],
    []
  );

  const handlePageChange = (newPage) => {
    if (isSearching) {
      if (newPage >= 1 && newPage <= searchPagination.totalPages) {
        setCurrentPage(newPage);
        handleSearch(searchQuery, newPage);
      }
    } else {
      if (newPage >= 1 && newPage <= pagination.totalPages) {
        setCurrentPage(newPage);
        fetchHiringRequests(newPage);
      }
    }
  };

  const handleSearch = async (query, page = 1) => {
    if (!query.trim()) {
      // Clear search and show regular results
      setIsSearching(false);
      setSearchQuery("");
      setSearchResults([]);
      setSearchPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        perPage: 15,
      });
      setCurrentPage(1);
      fetchHiringRequests(1);
      return;
    }

    try {
      setSearchLoading(true);
      setIsSearching(true);
      const token = localStorage.getItem("authToken");

      if (!token) {
        setNotification({
          message: "Authentication token not found. Please login again.",
          type: "error",
        });
        setSearchLoading(false);
        return;
      }

      const axiosInstance = axios.create({
        baseURL: "https://hrmspayroll.prepstripe.com/api/",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const response = await axiosInstance.get("recruitment-requests/search", {
        params: {
          search: query.trim(),
          page: page,
        },
      });

      // Debug: Log response structure (remove in production if needed)
      // console.log('Search API Response:', response.data);

      // Handle different response structures
      if (response?.status === 200) {
        // Check if response has success wrapper
        if (response.data?.success && response.data?.data) {
          const searchData = response.data.data;
          setSearchResults(searchData.data || []);
          setSearchPagination({
            currentPage: searchData.current_page || 1,
            totalPages: searchData.last_page || 1,
            totalItems: searchData.total || 0,
            perPage: searchData.per_page || 15,
          });
          setCurrentPage(searchData.current_page || 1);
        }
        // Handle case where data is directly in response.data
        else if (response.data?.data && Array.isArray(response.data.data)) {
          setSearchResults(response.data.data || []);
          setSearchPagination({
            currentPage:
              response.data.current_page ||
              response.data.meta?.current_page ||
              1,
            totalPages:
              response.data.last_page || response.data.meta?.last_page || 1,
            totalItems: response.data.total || response.data.meta?.total || 0,
            perPage:
              response.data.per_page || response.data.meta?.per_page || 15,
          });
          setCurrentPage(
            response.data.current_page || response.data.meta?.current_page || 1
          );
        }
        // Handle case where pagination data is nested differently
        else if (
          response.data?.data?.data &&
          Array.isArray(response.data.data.data)
        ) {
          const searchData = response.data.data;
          setSearchResults(searchData.data || []);
          setSearchPagination({
            currentPage: searchData.current_page || 1,
            totalPages: searchData.last_page || 1,
            totalItems: searchData.total || 0,
            perPage: searchData.per_page || 15,
          });
          setCurrentPage(searchData.current_page || 1);
        } else {
          // No results or unexpected structure
          setSearchResults([]);
          setSearchPagination({
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            perPage: 15,
          });
          if (response.data?.message) {
            setNotification({
              message: response.data.message,
              type: "info",
            });
          }
        }
      } else {
        setNotification({
          message:
            response?.data?.message || "Search failed. Please try again.",
          type: "error",
        });
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error searching hiring requests:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to search hiring requests.";
      setNotification({
        message: errorMessage,
        type: "error",
      });
      setSearchResults([]);
      setIsSearching(false);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    // Debounced search will be triggered by useEffect
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Clear any pending timeout and search immediately
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (searchQuery.trim()) {
      handleSearch(searchQuery.trim(), 1);
    }
  };

  const clearSearch = () => {
    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchQuery("");
    setIsSearching(false);
    setSearchResults([]);
    setSearchPagination({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      perPage: 15,
    });
    setCurrentPage(1);
    fetchHiringRequests(1);
  };

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-950 font-poppins rounded-lg">
      {/* notification compo */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
          Hiring Requests
        </h2>
        {isManager && (
          <button
            onClick={openModalForAdd}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto"
          >
            Add Hiring Request
          </button>
        )}
      </div>

      {/* Search Input */}
      <div className="mb-4">
        <form onSubmit={handleSearchSubmit} className="relative w-full sm:w-96">
          <Search className="absolute top-1/2 left-3 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchInputChange}
            placeholder="Search hiring requests..."
            className="w-full dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:outline-none focus:border-blue-700 dark:focus:outline-none placeholder:text-xs md:placeholder:text-sm placeholder:text-gray-600 pl-10 pr-10 py-2 border border-gray-300 rounded-md"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </form>
      </div>

      {/* data table */}
      <div>
        <ReusableTable
          data={isSearching ? searchResults : requests}
          loading={isSearching ? searchLoading : loading}
          columns={columns}
          searchable={false}
          filterable={false}
          excludeFilterColumns={excludeFilterColumns}
          onFilterChange={(filtering) => setIsFiltering(filtering)}
        />

        {/* Pagination */}
        {!isFiltering &&
          ((isSearching && searchResults?.length > 0) ||
            (!isSearching && requests?.length > 0)) && (
            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={(event) => handlePageChange(event.selected + 1)}
              forcePage={currentPage - 1}
              pageRangeDisplayed={5}
              disabledClassName="opacity-50 cursor-not-allowed"
              disabledLinkClassName="pointer-events-none"
              pageCount={
                isSearching
                  ? searchPagination.totalPages
                  : pagination.totalPages
              }
              previousLabel="< Prev"
              previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
              previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
              nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
              nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
              renderOnZeroPageCount={null}
              containerClassName="flex items-center justify-center mt-4 font-poppins"
              pageClassName="mx-1 border rounded dark:text-white font-poppins"
              pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
              activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
              activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
            />
          )}
      </div>

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={
          editingRequest ? "Edit Hiring Request" : "Add New Hiring Request"
        }
        fullSize={false}
      >
        <HiringRequestForm
          closeModal={closeModal}
          request={editingRequest}
          isEditing={Boolean(editingRequest)}
          setNotification={setNotification}
        />
      </Modal>

      {/* Modal for Campaign Conversion */}
      <Modal
        isOpen={isCampaignModalOpen}
        onRequestClose={closeCampaignModal}
        title="Convert to Campaign"
        fullSize={false}
      >
        {selectedRequestForCampaign && (
          <CampaignForm
            closeModal={closeCampaignModal}
            onSubmit={handleCampaignSubmit}
            initialValues={selectedRequestForCampaign}
            mode="Convert"
          />
        )}
      </Modal>

      {/* Modal for Reverting Hiring Request */}
      <Modal
        isOpen={isRevertModalOpen}
        onRequestClose={closeRevertModal}
        title="Revert Hiring Request"
        fullSize={false}
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Feedback <span className="text-red-500">*</span>
          </label>
          <textarea
            value={revertFeedback}
            onChange={(e) => setRevertFeedback(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Please provide feedback for reverting this request..."
            required
          />
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={closeRevertModal}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRevertSubmit}
              className="px-4 py-2 text-white bg-orange-600 rounded-md hover:bg-orange-700"
            >
              Revert
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal for Cancelling Hiring Request */}
      <Modal
        isOpen={isCancelModalOpen}
        onRequestClose={closeCancelModal}
        title="Cancel Hiring Request"
        fullSize={false}
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Feedback{" "}
            <span className="text-gray-500 dark:text-gray-400 text-xs">
              (optional)
            </span>
          </label>
          <textarea
            value={cancelFeedback}
            onChange={(e) => setCancelFeedback(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
            placeholder="Please provide a reason for cancelling this request (optional)..."
          />
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={closeCancelModal}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleCancelSubmit}
              className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
            >
              Cancel Request
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal for Viewing Revert Feedback */}
      <Modal
        isOpen={isFeedbackModalOpen}
        onRequestClose={closeFeedbackModal}
        title="Revert Feedback"
        fullSize={false}
      >
        <div className="space-y-4">
          {selectedRequestForFeedback && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Feedback
                </label>
                <div className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 min-h-[100px]">
                  {selectedRequestForFeedback.revert_feedback ||
                    "No feedback provided"}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Actioned By
                </label>
                <div className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100">
                  {selectedRequestForFeedback.actioned_by || "Not specified"}
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={closeFeedbackModal}
                  className="px-4 py-2 text-gray-600 bg-gray-200 dark:bg-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Drawer for Hiring request Details */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        border_radius="md:rounded-none"
        showClose={false}
        ChilDiv=""
        ChildParent=""
      >
        <HiringRequestDetails
          hiringRequest={selectedHiringRequest}
          hiring_request_id={selectedHiringRequest?.id}
          close={closeDrawer}
          user={user}
          roles={roles}
          onEdit={openModalForEdit}
          onDelete={openCancelModal}
          onFeedback={openFeedbackModal}
          onRevert={openRevertModal}
          onConvertToCampaign={openCampaignModal}
        />
      </Drawer>

      {/* Custom Alert Dialog */}
      <AlertDialog
        isOpen={showDeleteDialog}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Hiring Request"
        message="Are you sure you want to delete this hiring request? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
});

export default HiringRequests;
